import { IconType } from "@edaz/edazkit/Icon";
import * as Sentry from "@sentry/react";
import ClientOAuth2, { Token } from "client-oauth2";
import { accessTokenKey, accessTokenStoreTimestamp, clientId, clientSecret, eshopPaymentAPIScope, scope, tokenUrl } from "config";
import { action, observable, when } from "mobx";
import LocalizationService from "./localizationService";
import NotificationService, { NotificationType } from "./notificationService";

export default class AuthorizationService {
  @observable authClient?: ClientOAuth2;
  @observable authorizationInProgress = false;
  @observable tokenRefreshCycleInProgress = false;

  private TOKEN_EXPIRATION_OFFSET = 30;
  private tokenRefreshTimeout?: number;

  constructor(private notificationService: NotificationService, private localizationService: LocalizationService) {
    this.initialize();
  }

  @action.bound
  initialize() {
    if (!clientId || !clientSecret || !tokenUrl || !scope) {
      Sentry.withScope(scope => {
        scope.setTag("AuthorizationService", "token session storage parse");
        Sentry.captureException(new Error("Authorization service ENV not configured properly"));
      });
      return;
    }

    this.authClient = new ClientOAuth2({
      clientId: clientId,
      clientSecret: clientSecret,
      accessTokenUri: tokenUrl,
      scopes: [scope, eshopPaymentAPIScope],
    });
  }

  token() {
    const tokenDataJSON = sessionStorage.getItem(accessTokenKey);

    try {
      if (!tokenDataJSON) {
        return undefined;
      }

      const data = this.authClient?.createToken(JSON.parse(tokenDataJSON));

      if (data && !this.isTokenExpired(data)) {
        return data;
      } else {
        sessionStorage.removeItem(accessTokenKey);
        sessionStorage.removeItem(accessTokenStoreTimestamp);
        return undefined;
      }
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag("AuthorizationService", "token session storage parse");
        Sentry.captureException(error);
      });
      return undefined;
    }
  }

  isTokenExpired(token: Token) {
    const timeout = this.getTimeout(token);

    return !timeout || timeout <= 0;
  }

  isAuthorized() {
    return this.token() !== undefined;
  }

  @action.bound
  async authorize() {
    if (this.authorizationInProgress) {
      await when(() => !this.authorizationInProgress);
      return;
    }

    this.authorizationInProgress = true;
    try {
      await this.getAccessToken();
    } catch (e) {
      try {
        await this.getAccessToken();
      } catch (e) {
        this.notificationService.add({
          type: NotificationType.ERROR,
          icon: IconType.Offline,
          title: this.localizationService.translateGeneral("errors.accessToken"),
          permanent: true,
        });
        Sentry.withScope(scope => {
          scope.setTag("AuthorizationService", "token fetch failed");
          Sentry.captureException(e);
        });
        throw new Error("Fetch of access token failed");
      }
    }
  }

  @action.bound
  async getAccessToken() {
    if (!this.authClient || !this.authClient.credentials) return;

    const token = await this.authClient.credentials.getToken();
    sessionStorage.setItem(accessTokenKey, JSON.stringify(token.data));
    sessionStorage.setItem(accessTokenStoreTimestamp, ((Date.now() / 1000) | 0).toString());

    this.authorizationInProgress = false;
    this.tokenRefreshCycleInProgress = false;
    await this.startAccessTokenRenewCycle();
  }

  @action.bound
  async startAccessTokenRenewCycle() {
    if (this.tokenRefreshCycleInProgress) return;

    const token = this.token();

    if (token === undefined) {
      await this.authorize();

      return;
    }

    const timeout = this.getTimeout(token);
    if (!timeout || timeout <= 0 || !this.isAuthorized()) {
      await this.authorize();

      return;
    }

    clearTimeout(this.tokenRefreshTimeout);
    this.tokenRefreshCycleInProgress = true;
    this.tokenRefreshTimeout = window.setTimeout(() => this.authorize(), timeout * 1000);
  }

  private static accessTokenStoreTimestamp() {
    return sessionStorage.getItem(accessTokenStoreTimestamp) || AuthorizationService.getNowTimestampInSeconds();
  }

  private static getNowTimestampInSeconds() {
    return (Date.now() / 1000) | 0;
  }

  @action.bound
  getTimeout(token: Token) {
    const timeout =
      +AuthorizationService.accessTokenStoreTimestamp() +
      (+token.data.expires_in - this.TOKEN_EXPIRATION_OFFSET) -
      AuthorizationService.getNowTimestampInSeconds();

    return timeout;
  }

  buildHeaders = (): { [key: string]: string } => {
    const header = this.authorizationHeader();
    return header
      ? {
          authorization: header,
        }
      : {};
  };

  authorizationHeader = () => {
    const token = this.token();

    return token ? `${token.tokenType} ${token.accessToken}` : undefined;
  };
}
